import type { PaginatedResponse } from "@/interfaces/paginatedResponse";
import axios from "@/lib/axios";
import { useUsersStore } from "@/stores/users";
import { useQuery } from "@tanstack/vue-query";
import _ from "lodash";
import { storeToRefs } from "pinia";
import { watch } from "vue";
import type { User } from "../interfaces/user";
import { UserFilters } from "../interfaces/userFilters";

interface Params extends UserFilters {
  includes?: string[];
  ids?: Array<number | string>;
  immediate?: boolean;
}

const getUsers = async (
  params: Params,
  page?: string,
): Promise<PaginatedResponse<User>> => {
  const { data } = await axios.get<PaginatedResponse<User>>(
    `/api/users?page=${page}`,
    {
      params: {
        search: params.search ? params.search : undefined,
        status: params.status !== "all" ? params.status : undefined,
        role: params.role !== "all" ? params.role : undefined,
        group: params.group !== 0 ? params.group : undefined,
        department: params.department !== 0 ? params.department : undefined,
        ids: params.ids ? params.ids : undefined,
      },
    },
  );

  return data;
};

const useUsers = (params?: Params) => {
  const { immediate = true } = params ?? {};
  const { setUsers, setPagination, setFiltersValue, setIncludes, setPage } =
    useUsersStore();
  const { users, page, pagination, filters } = storeToRefs(useUsersStore());

  const { data, isError, isLoading, isFetching, refetch } = useQuery(
    ["users"],
    () => getUsers({ ...filters.value, ...(params ?? {}) }, page.value),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: immediate,
    },
  );

  const searchUsers = async () => {
    await refetch();
  };

  watch(
    data,
    (response) => {
      if (response) {
        setUsers(response.data);
        setPagination(_.omit(response, ["data"]));
      }
    },
    { immediate },
  );

  watch(
    filters,
    () => {
      searchUsers();
    },
    { deep: true, immediate },
  );

  return {
    users,
    filters,
    pagination,
    page,
    isError,
    isFetching,
    isLoading,

    setFiltersValue,
    refetch,
    searchUsers,
    setIncludes,
    setPage,
  };
};

export default useUsers;
